import {
  Button,
  Card,
  CardContent,
  Col,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Row,
  Select,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect, useMemo } from "react";

export type RetailerMatchRuleFormMode = "create" | "edit";

export interface RetailerMatchRuleFormValues {
  matchPattern: string;
  matchPriority: string;
  sourceColumn: string;
}

interface MasterDataRetailerMatchRuleAddEditFormProps {
  form: FormInstance<RetailerMatchRuleFormValues>;
  mode: RetailerMatchRuleFormMode;
  initialValues: RetailerMatchRuleFormValues;
  lockedRetailerName: string;
  isSaving: boolean;
  isFormSubmittable: boolean;
  onSubmit: (values: RetailerMatchRuleFormValues) => Promise<void> | void;
  onCancel: () => void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
  sourceColumns: readonly string[];
}

function parseSelectValue(value: unknown): string {
  if (value == null) return "";

  const raw =
    typeof value === "object" && "value" in (value as object)
      ? (value as SelectOption).value
      : value;

  if (typeof raw === "string") return raw;
  if (
    typeof raw === "number" ||
    typeof raw === "boolean" ||
    typeof raw === "bigint"
  ) {
    return String(raw);
  }
  return "";
}

interface SourceColumnSelectFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  options: SelectOption[];
  selectKey: string;
}

const SourceColumnSelectField = ({
  value,
  onChange,
  options,
  selectKey,
}: SourceColumnSelectFieldProps) => {
  const selected = options.find((option) => option.value === value);
  return (
    <Select
      key={`${selectKey}-${value ?? "none"}`}
      size="medium"
      isSearchable={false}
      isClearable={false}
      placeholder="Source column"
      options={options}
      defaultValue={selected}
      onChange={(next) => onChange?.(parseSelectValue(next))}
      aria-label="Source column"
    />
  );
};

export const MasterDataRetailerMatchRuleAddEditForm = ({
  form,
  mode,
  initialValues,
  lockedRetailerName,
  isSaving,
  isFormSubmittable,
  onSubmit,
  onCancel,
  onFormValidityChange,
  sourceColumns,
}: MasterDataRetailerMatchRuleAddEditFormProps) => {
  const sourceOptions = useMemo<SelectOption[]>(() => {
    const values = [...sourceColumns];
    const current = initialValues.sourceColumn?.trim() ?? "";
    if (current !== "" && !values.includes(current)) {
      values.push(current);
    }
    return values.map((column) => ({
      value: column,
      label: column,
    }));
  }, [initialValues.sourceColumn, sourceColumns]);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const matchPattern = values.matchPattern?.trim() ?? "";
    const matchPriority = values.matchPriority?.trim() ?? "";
    const sourceColumn = values.sourceColumn?.trim() ?? "";
    const priorityNumber = Number(matchPriority);
    const hasRequiredValues =
      matchPattern !== "" &&
      sourceColumn !== "" &&
      Number.isInteger(priorityNumber) &&
      priorityNumber >= 0;

    const isDirty =
      mode === "create"
        ? true
        : matchPattern !== (initialValues.matchPattern?.trim() ?? "") ||
          matchPriority !== (initialValues.matchPriority?.trim() ?? "") ||
          sourceColumn !== (initialValues.sourceColumn?.trim() ?? "");

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  return (
    <ErrorBoundary>
      <Card className="bg-white shadow-sm border-1 border-color-grey-25 rounded-medium mb-2">
        <CardContent className="p-3">
          <Form<RetailerMatchRuleFormValues>
            form={form}
            onFinish={onSubmit}
            onFieldsChange={updateFormValidity}
            className="master-data-dc-add-edit"
          >
            <Flex align="center" justifyContent="space-between" gap={1}>
              <Row style={{ flex: 1 }}>
                <Col span={3}>
                  <Form.Item>
                    <Input
                      value={lockedRetailerName}
                      isDisabled
                      aria-label="Retailer"
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    name="matchPattern"
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      { required: true, message: "Match pattern is required" },
                    ]}
                  >
                    <Input placeholder="Match pattern" />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item
                    name="matchPriority"
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      { required: true, message: "Match priority is required" },
                    ]}
                  >
                    <Input placeholder="Priority" />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="sourceColumn"
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      { required: true, message: "Source column is required" },
                    ]}
                  >
                    <SourceColumnSelectField
                      options={sourceOptions}
                      selectKey={`source-column-${mode}-${initialValues.sourceColumn}`}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Flex align="center" gap={0.5}>
                <Button
                  size="small"
                  color="primary"
                  variant="clear"
                  onPress={() => form.submit()}
                  isLoading={isSaving}
                  isDisabled={isSaving || !isFormSubmittable}
                  aria-label="Save match rule"
                >
                  <Icon name={Icons.Check} />
                </Button>
                <Button
                  size="small"
                  color="grey"
                  variant="clear"
                  onPress={onCancel}
                  isDisabled={isSaving}
                  aria-label="Cancel"
                >
                  <Icon name={Icons.Close} />
                </Button>
              </Flex>
            </Flex>
          </Form>
        </CardContent>
      </Card>
    </ErrorBoundary>
  );
};
